import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface PaymentGatewayConfig {
  id: string;
  provider: string;
  merchantKey: string;
  merchantSecret: string;
  webhookSecret?: string;
  mode: 'TEST' | 'LIVE';
  isActive: boolean;
}

const PROVIDERS = ['Razorpay', 'PayU', 'Stripe', 'Cashfree', 'Custom'];

const PaymentGatewaySettings: React.FC = () => {
  const [configs, setConfigs] = useState<PaymentGatewayConfig[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PaymentGatewayConfig | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  
  const [formData, setFormData] = useState({
    provider: '',
    merchantKey: '',
    merchantSecret: '',
    webhookSecret: '',
    mode: 'TEST',
    isActive: false,
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get('/system-settings/payments/gateway');
      setConfigs(response.data);
    } catch (error) {
      console.error('Failed to fetch gateway configs', error);
    }
  };

  const handleOpenDialog = (config?: PaymentGatewayConfig) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        provider: config.provider,
        merchantKey: config.merchantKey,
        merchantSecret: '', // Don't pre-fill secret for security, user must re-enter to change
        webhookSecret: '',
        mode: config.mode as any,
        isActive: config.isActive,
      });
    } else {
      setEditingConfig(null);
      setFormData({
        provider: '',
        merchantKey: '',
        merchantSecret: '',
        webhookSecret: '',
        mode: 'TEST',
        isActive: false,
      });
    }
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingConfig) {
        const payload: any = { ...formData };
        // Only send secrets if they are provided (not empty string)
        if (!payload.merchantSecret) delete payload.merchantSecret;
        if (!payload.webhookSecret) delete payload.webhookSecret;

        await axios.patch(`/system-settings/payments/gateway/${editingConfig.id}`, payload);
      } else {
        await axios.post('/system-settings/payments/gateway', formData);
      }
      fetchConfigs();
      setOpenDialog(false);
    } catch (error) {
      console.error('Failed to save config', error);
      alert('Failed to save configuration. Please check inputs.');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this gateway configuration?')) {
      try {
        await axios.delete(`/system-settings/payments/gateway/${id}`);
        fetchConfigs();
      } catch (error: any) {
        alert(error.response?.data?.message || 'Failed to delete configuration');
      }
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      if (currentStatus) {
        await axios.patch(`/system-settings/payments/gateway/${id}/deactivate`);
      } else {
        await axios.patch(`/system-settings/payments/gateway/${id}/activate`);
      }
      fetchConfigs();
    } catch (error) {
      console.error('Failed to toggle status', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Payment Gateway Configurations</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Gateway
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Provider</TableCell>
              <TableCell>Merchant Key</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((config) => (
              <TableRow key={config.id}>
                <TableCell>{config.provider}</TableCell>
                <TableCell>{config.merchantKey}</TableCell>
                <TableCell>
                  <Chip 
                    label={config.mode} 
                    color={config.mode === 'LIVE' ? 'error' : 'warning'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.isActive}
                        onChange={() => handleToggleActive(config.id, config.isActive)}
                      />
                    }
                    label={config.isActive ? 'Active' : 'Inactive'}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(config)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(config.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {configs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No payment gateways configured
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingConfig ? 'Edit Gateway' : 'Add Gateway'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="warning">
                Ensure you are entering correct credentials. For security, existing secrets are hidden.
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Provider"
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              >
                {PROVIDERS.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Mode"
                value={formData.mode}
                onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
              >
                <MenuItem value="TEST">TEST</MenuItem>
                <MenuItem value="LIVE">LIVE</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Merchant Key"
                value={formData.merchantKey}
                onChange={(e) => setFormData({ ...formData, merchantKey: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type={showSecret ? 'text' : 'password'}
                label="Merchant Secret"
                value={formData.merchantSecret}
                placeholder={editingConfig ? 'Leave empty to keep existing' : ''}
                onChange={(e) => setFormData({ ...formData, merchantSecret: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowSecret(!showSecret)}>
                      {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type={showSecret ? 'text' : 'password'}
                label="Webhook Secret (Optional)"
                value={formData.webhookSecret}
                placeholder={editingConfig ? 'Leave empty to keep existing' : ''}
                onChange={(e) => setFormData({ ...formData, webhookSecret: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Set as Active Gateway"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentGatewaySettings;
